import { baseApi } from 'app/services/baseApi';
import {
  OpportunityResponse,
  OpportunitiesResponse,
  UsersAndContactsResponse,
  OrganizationUser,
  Contact,
  QuotesByOpportunityIdResponse,
  ContractsByOpportunityIdResponse,
} from './salesforceTypes';

const salesforceApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getAllOpportunities: builder.query<OpportunitiesResponse, string | void>({
      query: (queryParams: string) => {
        if (!queryParams)
          return {
            url: 'salesforce/self/availableOpportunities',
            method: 'GET',
          };
        return {
          url: `salesforce/self/availableOpportunities?${queryParams}`,
          method: 'GET',
        };
      },
    }),
    getOpportunityById: builder.query<OpportunityResponse, string | void>({
      query: opportunityId => {
        return {
          url: `/salesforce/self/opportunity/${opportunityId}`,
          method: 'GET',
        };
      },
    }),
    getAllLoans: builder.query({
      query: (queryParams: string) => {
        if (!queryParams)
          return {
            url: `salesforce/self/loans`,
            method: 'GET',
          };
        return {
          url: `salesforce/self/loans?${queryParams}`,
          method: 'GET',
        };
      },
    }),
    getLoanById: builder.query({
      query: loanId => {
        return {
          url: `/salesforce/self/loan/${loanId}`,
          method: 'GET',
        };
      },
    }),
    getQuotesByOpportunityId: builder.query<QuotesByOpportunityIdResponse, string>({
      query: opportunityId => {
        return {
          url: `salesforce/self/opportunity/${opportunityId}/quotes`,
          method: 'GET',
        };
      },
    }),
    getContractsByOpportunityId: builder.query<ContractsByOpportunityIdResponse, string>({
      query: opportunityId => {
        return {
          url: `salesforce/self/opportunity/${opportunityId}/contracts`,
          method: 'GET',
        };
      },
    }),
    getContactsAndUsers: builder.query({
      query: (queryParams: string) => {
        if (!queryParams)
          return {
            url: 'salesforce/self/contactsAndUsers',
            method: 'GET',
          };
        return {
          url: `salesforce/self/contactsAndUsers?${queryParams}`,
          method: 'GET',
        };
      },
      transformResponse: (response: UsersAndContactsResponse) => {
        const { organizationUsers } = response.data.users;
        const { contacts } = response.data.contacts;

        const mapUser = ({ Id, Name, Email }: OrganizationUser) => ({
          id: Id,
          name: Name,
          email: Email,
        });

        const mapContact = ({ Id, Name, Email }: Contact) => ({
          id: Id,
          name: Name,
          email: Email,
        });

        const mappedUsers = organizationUsers.map(mapUser);
        const mappedContacts = contacts.map(mapContact);

        return [...mappedUsers, ...mappedContacts];
      },
    }),
    putProductInQuote: builder.mutation({
      query: ({ ...data }) => {
        return {
          url: `salesforce/self/quote/updateProductInQuote`,
          body: data,
          method: 'PUT',
        };
      },
    }),
  }),
});

export const {
  useLazyGetOpportunityByIdQuery,
  useGetAllOpportunitiesQuery,
  useGetQuotesByOpportunityIdQuery,
  useGetContractsByOpportunityIdQuery,
  useGetContactsAndUsersQuery,
  usePutProductInQuoteMutation,
  useGetAllLoansQuery,
  useLazyGetLoanByIdQuery,
} = salesforceApi;
